import { useState, useEffect, useRef } from "react";
import { api, errMsg, uploadVideoChunked } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, UploadCloud, HardDrive, Cloud, DollarSign, RefreshCw } from "lucide-react";

const fmtBytes = (b) => {
  if (b >= 1024 ** 3) return (b / 1024 ** 3).toFixed(2) + " GB";
  if (b >= 1048576) return (b / 1048576).toFixed(1) + " MB";
  return Math.round(b / 1024) + " KB";
};

export default function AdminStorage() {
  const [data, setData] = useState(null);
  const [uploads, setUploads] = useState({});
  const inputRef = useRef(null);

  const load = () => api.get("/admin/storage").then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); setData({ files: [] }); });
  useEffect(() => { load(); }, []);

  const uploadFiles = async (list) => {
    for (const file of Array.from(list)) {
      const key = file.name + Date.now();
      setUploads((u) => ({ ...u, [key]: { name: file.name, pct: 0 } }));
      try {
        await uploadVideoChunked(file, (pct) => setUploads((u) => ({ ...u, [key]: { name: file.name, pct } })));
        toast.success(`${file.name} uploaded to Cloudflare R2 ✓`);
      } catch (e) { toast.error(`${file.name}: ${errMsg(e)}`); }
      setUploads((u) => { const n = { ...u }; delete n[key]; return n; });
    }
    load();
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.filename}" permanently? This frees ${fmtBytes(f.total_size)} on Cloudflare R2 and cannot be undone.`)) return;
    try { await api.delete(`/admin/media/${f.id}`); toast.success("Video deleted — R2 storage is being freed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const removeExternal = async (o) => {
    if (!window.confirm(`Delete "${o.key}" permanently from your Cloudflare R2 bucket? This frees ${fmtBytes(o.size)} and cannot be undone.`)) return;
    try {
      await api.delete(`/admin/storage/object?key=${encodeURIComponent(o.key)}`);
      toast.success("Deleted from Cloudflare R2 ✓");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const pctUsed = data && data.free_tier_gb ? Math.min(100, (data.total_gb / data.free_tier_gb) * 100) : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-display font-bold text-xl" data-testid="admin-storage-title">CLOUDFLARE R2 STORAGE</h1>
        <button className="btn-ghost !py-1 !px-3 text-xs flex items-center gap-1" data-testid="storage-refresh-btn"
          onClick={() => { setData(null); load(); }}><RefreshCw size={13} /> Refresh</button>
      </div>
      <p className="text-sm mb-5" style={{ color: "var(--muted)" }}>Live view of your R2 bucket{data?.bucket ? ` "${data.bucket}"` : ""} — totals are read straight from Cloudflare, and deleting here removes the actual files from R2.</p>

      {data === null ? <p style={{ color: "var(--muted)" }}>Loading…</p> : (
        <>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <div className="forge-card p-4" data-testid="storage-total-card">
              <div className="flex items-center gap-2 text-xs mb-1" style={{ color: "var(--muted)" }}><HardDrive size={14} /> TOTAL STORED</div>
              <p className="font-display font-bold text-2xl">{fmtBytes(data.total_bytes)}</p>
              <div className="mt-2" style={{ height: 6, background: "var(--line)", borderRadius: 3 }}>
                <div style={{ height: "100%", width: `${pctUsed}%`, background: pctUsed > 90 ? "#f87171" : "var(--gold)", borderRadius: 3 }} />
              </div>
              <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>{data.total_gb} GB of {data.free_tier_gb} GB free tier</p>
            </div>
            <div className="forge-card p-4" data-testid="storage-free-card">
              <div className="flex items-center gap-2 text-xs mb-1" style={{ color: "var(--muted)" }}><Cloud size={14} /> FREE TIER LEFT</div>
              <p className="font-display font-bold text-2xl" style={{ color: "var(--bp)" }}>{data.free_remaining_gb} GB</p>
              <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>Streaming to students is always free (0 egress fees)</p>
            </div>
            <div className="forge-card p-4" data-testid="storage-cost-card">
              <div className="flex items-center gap-2 text-xs mb-1" style={{ color: "var(--muted)" }}><DollarSign size={14} /> EST. MONTHLY COST</div>
              <p className="font-display font-bold text-2xl" style={{ color: data.est_monthly_cost > 0 ? "var(--gold)" : "var(--land)" }}>
                {data.est_monthly_cost > 0 ? `$${data.est_monthly_cost.toFixed(2)}` : "$0.00"}
              </p>
              <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>${data.price_per_gb}/GB beyond {data.free_tier_gb} GB · {data.billable_gb} GB billable</p>
            </div>
          </div>

          <label className="flex flex-col items-center justify-center gap-2 p-6 mb-6 rounded-xl cursor-pointer transition-colors"
            style={{ border: "2px dashed var(--line)", color: "var(--muted)" }} data-testid="storage-upload-zone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files); }}>
            <UploadCloud size={26} style={{ color: "var(--gold)" }} />
            <span className="font-display text-sm" style={{ letterSpacing: 1 }}>DROP VIDEOS HERE OR CLICK — UPLOADS STRAIGHT TO R2</span>
            <span className="text-xs">mp4, webm, mov, mkv · up to 2GB each</span>
            <input ref={inputRef} type="file" multiple hidden accept="video/mp4,video/webm,video/quicktime,video/x-matroska"
              data-testid="storage-upload-input" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
          </label>

          {Object.entries(uploads).map(([k, u]) => (
            <div key={k} className="forge-card p-3 mb-2 text-sm" data-testid="storage-upload-progress">
              <div className="flex justify-between mb-1"><span>{u.name}</span><span style={{ color: "var(--gold)" }}>{u.pct}%</span></div>
              <div style={{ height: 5, background: "var(--line)", borderRadius: 3 }}>
                <div style={{ height: "100%", width: `${u.pct}%`, background: "var(--gold)", borderRadius: 3, transition: "width .2s" }} />
              </div>
            </div>
          ))}

          {data.bucket_error && (
            <p className="text-sm mb-4" style={{ color: "#f87171" }} data-testid="storage-bucket-error">{data.bucket_error}</p>
          )}
          {data.files.length === 0 && (data.external || []).length === 0 && (
            <div className="empty" data-testid="storage-empty">No videos in storage yet.</div>
          )}
          {data.files.length > 0 && (
            <div className="forge-card overflow-x-auto">
              <table className="w-full text-sm" data-testid="storage-table">
                <thead>
                  <tr className="text-left text-xs" style={{ color: "var(--muted)", borderBottom: "1px solid var(--line)" }}>
                    <th className="p-3">VIDEO</th>
                    <th className="p-3">ORIGINAL</th>
                    <th className="p-3">QUALITIES</th>
                    <th className="p-3">TOTAL</th>
                    <th className="p-3">USED IN</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.files.map((f) => (
                    <tr key={f.id} data-testid={`storage-row-${f.id}`} style={{ borderBottom: "1px solid var(--line)" }}>
                      <td className="p-3 max-w-[220px]">
                        <p className="truncate font-medium" title={f.filename}>{f.filename}</p>
                        <p className="text-xs" style={{ color: "var(--muted)" }}>{new Date(f.created_at).toLocaleDateString()}{f.on_r2 ? " · on R2" : ""}</p>
                      </td>
                      <td className="p-3 whitespace-nowrap">{fmtBytes(f.original_size)}</td>
                      <td className="p-3 text-xs" style={{ color: "var(--bp)" }}>
                        {f.transcode_status === "processing" ? "⏳ generating…"
                          : Object.keys(f.renditions).length
                            ? Object.entries(f.renditions).sort((a, b) => b[0] - a[0]).map(([h, s]) => `${h}p ${fmtBytes(s)}`).join(" · ")
                            : "—"}
                      </td>
                      <td className="p-3 whitespace-nowrap font-medium" style={{ color: "var(--gold)" }}>{fmtBytes(f.total_size)}</td>
                      <td className="p-3 text-xs max-w-[200px]">
                        {f.usage.length ? <span style={{ color: "var(--land)" }}>{f.usage.map((u) => u.lecture).join(" · ")}</span>
                          : <span style={{ color: "var(--muted)" }}>unused</span>}
                      </td>
                      <td className="p-3">
                        <button className="btn-ghost !py-1 !px-2 text-xs hover:!text-red-400" data-testid={`storage-delete-${f.id}`}
                          title={f.usage.length ? "Remove from lectures first" : "Delete from R2"}
                          onClick={() => remove(f)}><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(data.external || []).length > 0 && (
            <div className="mt-6">
              <h2 className="font-display font-bold text-sm mb-1" style={{ letterSpacing: 1 }}>UPLOADED DIRECTLY TO CLOUDFLARE</h2>
              <p className="text-xs mb-3" style={{ color: "var(--muted)" }}>
                These files were added straight to the bucket (e.g. via the Cloudflare dashboard). They count toward your storage, but to use a video in a lecture with protected playback, upload it through this page or the Media library.
              </p>
              <div className="forge-card overflow-x-auto">
                <table className="w-full text-sm" data-testid="storage-external-table">
                  <thead>
                    <tr className="text-left text-xs" style={{ color: "var(--muted)", borderBottom: "1px solid var(--line)" }}>
                      <th className="p-3">FILE</th>
                      <th className="p-3">SIZE</th>
                      <th className="p-3">MODIFIED</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.external.map((o) => (
                      <tr key={o.key} data-testid={`storage-ext-row-${o.key}`} style={{ borderBottom: "1px solid var(--line)" }}>
                        <td className="p-3 max-w-[380px]"><p className="truncate" title={o.key}>{o.key}</p></td>
                        <td className="p-3 whitespace-nowrap font-medium" style={{ color: "var(--gold)" }}>{fmtBytes(o.size)}</td>
                        <td className="p-3 text-xs whitespace-nowrap" style={{ color: "var(--muted)" }}>{new Date(o.modified).toLocaleDateString()}</td>
                        <td className="p-3">
                          <button className="btn-ghost !py-1 !px-2 text-xs hover:!text-red-400" data-testid={`storage-ext-delete-${o.key}`}
                            title="Delete from R2" onClick={() => removeExternal(o)}><Trash2 size={13} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
